import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, AlertTriangle, ShieldCheck, Trash2, RefreshCw, FileWarning, Database, Wrench, FileDown, Package, FileText, Clock, Boxes, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dev/joomla911`;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

const SEVERITY_COLORS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-gray-400 text-white',
};

const VERDICT_COLORS = {
  malicious: 'bg-red-600 text-white',
  suspicious: 'bg-orange-500 text-white',
  clean: 'bg-green-600 text-white',
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DevJoomla911Scanner = () => {
  const [scans, setScans] = useState([]);
  const [activeScan, setActiveScan] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyzing, setAnalyzing] = useState({});
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [bulkResolving, setBulkResolving] = useState(false);
  const [components, setComponents] = useState({});
  const [componentThreshold, setComponentThreshold] = useState(3);
  const [analyzingComponent, setAnalyzingComponent] = useState({});
  const [removingComponent, setRemovingComponent] = useState({});
  const [selectedTables, setSelectedTables] = useState({});
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const bulkPollRef = useRef(null);
  const finalizePollRef = useRef(null);

  const loadScans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scans`, { headers: authHeaders() });
      setScans(res.data.scans || []);
    } catch (error) {
      // Feature flag likely disabled — surfaced via the upload button instead of a toast on load
    }
  }, []);

  useEffect(() => { loadScans(); }, [loadScans]);

  const loadComponents = useCallback(async (scanId) => {
    try {
      const res = await axios.get(`${API}/scans/${scanId}/components`, { headers: authHeaders() });
      setComponents(res.data.components || {});
      setComponentThreshold(res.data.threshold || 3);
    } catch (error) {
      setComponents({});
    }
  }, []);

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(bulkPollRef.current);
    clearInterval(finalizePollRef.current);
  }, []);

  const pollScan = useCallback((scanId) => {
    clearInterval(pollRef.current);
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/scans/${scanId}`, { headers: authHeaders() });
        setActiveScan(res.data);
        if (['completed', 'ai_review', 'failed'].includes(res.data.status)) {
          clearInterval(pollRef.current);
          setPolling(false);
          loadScans();
          if (res.data.status !== 'failed') loadComponents(scanId);
        }
      } catch (error) {
        clearInterval(pollRef.current);
        setPolling(false);
      }
    }, 2000);
  }, [loadScans, loadComponents]);

  const handleFileSelect = async (file) => {
    if (!file) return;
    const validExt = /\.(zip|rar|jpa|jps)$/i.test(file.name);
    if (!validExt) {
      toast({ title: 'Unsupported File', description: 'Please upload a .zip, .rar, or .jpa/.jps archive.', variant: 'destructive' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const initRes = await axios.post(`${API}/upload/init`, { filename: file.name, total_size: file.size }, { headers: authHeaders() });
      const { upload_id } = initRes.data;

      let offset = 0;
      let chunkIndex = 0;
      const MAX_CHUNK_RETRIES = 4;
      while (offset < file.size) {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        const expectedReceived = offset + chunk.size;
        let attempt = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          attempt += 1;
          try {
            const form = new FormData();
            form.append('upload_id', upload_id);
            form.append('chunk_index', chunkIndex);
            form.append('chunk', chunk, file.name);
            const chunkRes = await axios.post(`${API}/upload/chunk`, form, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
            // Never trust a 200 status alone — cross-check the server's own
            // reported (ground-truth, on-disk) byte count against what THIS
            // chunk should have brought the total to. A network hiccup, proxy
            // timeout, or dropped connection can silently short-write a chunk
            // while the request itself still resolves — catching that here
            // means retrying just this one chunk instead of failing the
            // entire upload after the rest of a possibly huge file is sent.
            if (chunkRes.data?.received_bytes !== expectedReceived) {
              throw new Error(`Chunk ${chunkIndex} did not persist correctly (server has ${chunkRes.data?.received_bytes} bytes, expected ${expectedReceived}).`);
            }
            break;
          } catch (chunkError) {
            if (attempt >= MAX_CHUNK_RETRIES) throw chunkError;
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
        offset += CHUNK_SIZE;
        chunkIndex += 1;
        setUploadProgress(Math.min(100, Math.round((offset / file.size) * 100)));
      }

      const completeRes = await axios.post(`${API}/upload/complete`, new URLSearchParams({ upload_id }), { headers: authHeaders() });
      const { scan_id } = completeRes.data;
      toast({ title: 'Upload complete', description: 'Extraction and local scan started…' });
      setActiveScan({ id: scan_id, status: 'queued', findings: [] });
      pollScan(scan_id);
    } catch (error) {
      toast({ title: 'Upload Failed', description: error.response?.data?.detail || 'Could not upload/scan this archive.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const openScan = async (scanId) => {
    try {
      const res = await axios.get(`${API}/scans/${scanId}`, { headers: authHeaders() });
      setActiveScan(res.data);
      setComponents({});
      if (res.data.status === 'scanning' || res.data.status === 'extracting' || res.data.status === 'queued') {
        pollScan(scanId);
      }
      if (['ai_review', 'completed'].includes(res.data.status)) {
        loadComponents(scanId);
      }
      if (res.data.bulk_analysis_status === 'running') {
        setAnalyzingAll(true);
        setBulkProgress(res.data.bulk_analysis_progress || null);
        clearInterval(bulkPollRef.current);
        bulkPollRef.current = setInterval(async () => {
          try {
            const scanRes = await axios.get(`${API}/scans/${scanId}`, { headers: authHeaders() });
            setActiveScan(scanRes.data);
            if (scanRes.data.bulk_analysis_progress) setBulkProgress(scanRes.data.bulk_analysis_progress);
            if (scanRes.data.bulk_analysis_status !== 'running') {
              clearInterval(bulkPollRef.current);
              setAnalyzingAll(false);
              setBulkProgress(null);
              loadScans();
            }
          } catch (error) {
            clearInterval(bulkPollRef.current);
            setAnalyzingAll(false);
            setBulkProgress(null);
          }
        }, 3000);
      }
      if (res.data.status === 'finalizing') {
        setFinalizing(true);
        clearInterval(finalizePollRef.current);
        finalizePollRef.current = setInterval(async () => {
          try {
            const scanRes = await axios.get(`${API}/scans/${scanId}`, { headers: authHeaders() });
            if (scanRes.data.status === 'finalized' || scanRes.data.status === 'completed') {
              clearInterval(finalizePollRef.current);
              setActiveScan(scanRes.data);
              setFinalizing(false);
              loadScans();
            }
          } catch (error) {
            clearInterval(finalizePollRef.current);
            setFinalizing(false);
          }
        }, 2000);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Could not load scan.', variant: 'destructive' });
    }
  };

  const analyzeFinding = async (findingId) => {
    setAnalyzing((prev) => ({ ...prev, [findingId]: true }));
    try {
      const res = await axios.post(`${API}/scans/${activeScan.id}/findings/${findingId}/analyze`, {}, { headers: authHeaders() });
      setActiveScan((prev) => ({
        ...prev,
        findings: prev.findings.map((f) => (f.id === findingId ? {
          ...f, ai_verdict: res.data.verdict, ai_confidence: res.data.confidence,
          ai_explanation: res.data.explanation, ai_recommendation: res.data.recommendation,
        } : f)),
      }));
    } catch (error) {
      toast({ title: 'AI Analysis Failed', description: error.response?.data?.detail || 'Could not analyze this finding.', variant: 'destructive' });
    } finally {
      setAnalyzing((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  const resolveFinding = async (findingId, decision, resolutionAction) => {
    try {
      const res = await axios.patch(
        `${API}/scans/${activeScan.id}/findings/${findingId}`,
        { admin_decision: decision, resolution_action: resolutionAction },
        { headers: authHeaders() }
      );
      setActiveScan((prev) => ({
        ...prev,
        findings: prev.findings.map((f) => (f.id === findingId ? { ...f, admin_decision: decision, resolution_action: resolutionAction || 'manual' } : f)),
        status: res.data.all_resolved ? 'completed' : prev.status,
      }));
      if (res.data.all_resolved) {
        setScans((prev) => prev.map((s) => (s.id === activeScan.id ? { ...s, status: 'completed' } : s)));
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Could not resolve finding.', variant: 'destructive' });
    }
  };

  const bulkResolve = async (filterVerdict, adminDecision, resolutionAction) => {
    setBulkResolving(true);
    try {
      const res = await axios.post(
        `${API}/scans/${activeScan.id}/bulk-resolve`,
        { filter_verdict: filterVerdict, admin_decision: adminDecision, resolution_action: resolutionAction },
        { headers: authHeaders() }
      );
      const scanRes = await axios.get(`${API}/scans/${activeScan.id}`, { headers: authHeaders() });
      setActiveScan(scanRes.data);
      if (res.data.all_resolved) {
        setScans((prev) => prev.map((s) => (s.id === activeScan.id ? { ...s, status: 'completed' } : s)));
      } else {
        loadScans();
      }
      toast({ title: 'Bulk Action Applied', description: `${res.data.count_resolved} finding(s) updated.` });
    } catch (error) {
      toast({ title: 'Bulk Action Failed', description: error.response?.data?.detail || 'Could not apply this bulk action.', variant: 'destructive' });
    } finally {
      setBulkResolving(false);
    }
  };

  const analyzeComponent = async (key) => {
    setAnalyzingComponent((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/scans/${activeScan.id}/components/${key}/analyze`, {}, { headers: authHeaders() });
      setComponents((prev) => ({ ...prev, [key]: { ...prev[key], ...res.data } }));
      setSelectedTables((prev) => ({ ...prev, [key]: res.data.ai_likely_tables || [] }));
    } catch (error) {
      toast({ title: 'Component Analysis Failed', description: error.response?.data?.detail || 'Could not analyze this component.', variant: 'destructive' });
    } finally {
      setAnalyzingComponent((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleComponentTable = (key, table) => {
    setSelectedTables((prev) => {
      const current = prev[key] || [];
      const next = current.includes(table) ? current.filter((t) => t !== table) : [...current, table];
      return { ...prev, [key]: next };
    });
  };

  const removeComponent = async (key) => {
    if (!window.confirm(`Permanently delete every file in "${key}" and strip the selected database table(s) from the SQL dump? This cannot be undone.`)) return;
    setRemovingComponent((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(
        `${API}/scans/${activeScan.id}/components/${key}/remove`,
        { table_names: selectedTables[key] || [] },
        { headers: authHeaders() }
      );
      setComponents((prev) => ({
        ...prev,
        [key]: { ...prev[key], removed: true, removed_at: new Date().toISOString(), tables_stripped: res.data.tables_stripped },
      }));
      const scanRes = await axios.get(`${API}/scans/${activeScan.id}`, { headers: authHeaders() });
      setActiveScan(scanRes.data);
      if (res.data.all_resolved) {
        setScans((prev) => prev.map((s) => (s.id === activeScan.id ? { ...s, status: 'completed' } : s)));
      } else {
        loadScans();
      }
      toast({ title: 'Component Removed', description: `${res.data.files_deleted} file(s) deleted, ${res.data.findings_resolved} finding(s) resolved.` });
    } catch (error) {
      toast({ title: 'Removal Failed', description: error.response?.data?.detail || 'Could not remove this component.', variant: 'destructive' });
    } finally {
      setRemovingComponent((prev) => ({ ...prev, [key]: false }));
    }
  };


  const finalizeScan = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${API}/scans/${activeScan.id}/finalize`, {}, { headers: authHeaders() });
      setActiveScan((prev) => ({ ...prev, status: 'finalizing' }));
      clearInterval(finalizePollRef.current);
      finalizePollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/scans/${activeScan.id}`, { headers: authHeaders() });
          if (res.data.status === 'finalized' || res.data.status === 'completed') {
            clearInterval(finalizePollRef.current);
            setActiveScan(res.data);
            setFinalizing(false);
            loadScans();
            if (res.data.status === 'finalized') {
              toast({ title: 'Scan Finalized', description: 'Fixes applied — your downloads are ready below.' });
            } else {
              toast({ title: 'Finalize Failed', description: res.data.finalize_error || 'Please try again.', variant: 'destructive' });
            }
          }
        } catch (error) {
          clearInterval(finalizePollRef.current);
          setFinalizing(false);
        }
      }, 2000);
    } catch (error) {
      toast({ title: 'Could Not Finalize', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
      setFinalizing(false);
    }
  };

  const downloadArtifact = async (artifact) => {
    try {
      const res = await axios.get(`${API}/scans/${activeScan.id}/download/${artifact}`, { headers: authHeaders(), responseType: 'blob' });
      const filenames = { 'fixed-files': 'fixed_files.zip', 'full-site': 'full_site_cleaned.zip', report: 'report.html' };
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filenames[artifact];
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response?.status === 410) {
        toast({ title: 'Download Expired', description: error.response?.data?.detail || 'This file was auto-deleted 15 days after the scan.', variant: 'destructive' });
      } else {
        toast({ title: 'Download Failed', description: 'Could not download this file.', variant: 'destructive' });
      }
    }
  };

  const analyzeAllFindings = async () => {
    const pendingCount = (activeScan.findings || []).filter((f) => !f.ai_verdict).length;
    if (pendingCount === 0) return;
    setAnalyzingAll(true);
    setBulkProgress({ done: 0, total: pendingCount });
    try {
      const res = await axios.post(`${API}/scans/${activeScan.id}/analyze-all`, {}, { headers: authHeaders() });
      if (!res.data.started) {
        setAnalyzingAll(false);
        setBulkProgress(null);
        return;
      }
      toast({ title: 'Bulk Analysis Started', description: `Analyzing ${res.data.total} finding(s) in the background — this may take a few minutes for large scans.` });

      clearInterval(bulkPollRef.current);
      bulkPollRef.current = setInterval(async () => {
        try {
          const scanRes = await axios.get(`${API}/scans/${activeScan.id}`, { headers: authHeaders() });
          setActiveScan(scanRes.data);
          if (scanRes.data.bulk_analysis_progress) setBulkProgress(scanRes.data.bulk_analysis_progress);
          if (scanRes.data.bulk_analysis_status !== 'running') {
            clearInterval(bulkPollRef.current);
            setAnalyzingAll(false);
            setBulkProgress(null);
            loadScans();
            toast({ title: 'Bulk Analysis Complete', description: 'All findings have been analyzed by AI.' });
          }
        } catch (error) {
          clearInterval(bulkPollRef.current);
          setAnalyzingAll(false);
          setBulkProgress(null);
        }
      }, 3000);
    } catch (error) {
      setAnalyzingAll(false);
      setBulkProgress(null);
      toast({ title: 'Bulk Analysis Failed', description: error.response?.data?.detail || 'Could not start bulk analysis.', variant: 'destructive' });
    }
  };

  const deleteScan = async (scanId) => {
    try {
      await axios.delete(`${API}/scans/${scanId}`, { headers: authHeaders() });
      if (activeScan?.id === scanId) setActiveScan(null);
      loadScans();
      toast({ title: 'Deleted', description: 'Scan removed.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Could not delete scan.', variant: 'destructive' });
    }
  };

  return (
    <div className="max-w-5xl" data-testid="dev-joomla911-scanner">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-red-600" />
          Joomla 911 Web Scanner <Badge variant="secondary">Super Admin Testing Tool</Badge>
        </h2>
        <p className="text-gray-500 mt-1">
          Upload a full site archive (.zip / .rar / .jpa Akeeba backup) — scans every file AND any SQL database dump for malware, then gets a Claude AI second opinion. Must be enabled in Feature Flags → Security.
        </p>
      </div>

      <Card className="mb-6" data-testid="j911-upload-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Upload className="w-5 h-5" /> Upload Archive</CardTitle>
          <CardDescription>Supports .zip, .rar, and Akeeba .jpa/.jps backups. ~89% of real uploads are expected to be Akeeba backups.</CardDescription>
        </CardHeader>
        <CardContent>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,.rar,.jpa,.jps"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
            data-testid="j911-file-input"
          />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-red-600 hover:bg-red-700" data-testid="j911-upload-button">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? `Uploading… ${uploadProgress}%` : 'Choose Archive & Scan'}
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-6" data-testid="j911-scans-list-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Recent Scans</CardTitle>
            <CardDescription>Uploaded archives and their scan results (originals + extracted files are deleted after scanning — only findings are kept)</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadScans} data-testid="j911-refresh-scans-button"><RefreshCw className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent>
          {scans.length === 0 ? (
            <p className="text-sm text-gray-500">No scans yet.</p>
          ) : (
            <div className="space-y-2">
              {scans.map((s) => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border" data-testid={`j911-scan-row-${s.id}`}>
                  <button className="text-left flex-1" onClick={() => openScan(s.id)} data-testid={`j911-scan-open-${s.id}`}>
                    <p className="font-medium text-sm">{s.original_filename} <span className="text-gray-400">({s.archive_type})</span></p>
                    <p className="text-xs text-gray-500">
                      {s.status} · {s.total_scanned} scanned · {s.total_flagged} flagged · {new Date(s.created_at).toLocaleString()}
                    </p>
                  </button>
                  <Badge className="mr-2">{s.status}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => deleteScan(s.id)} data-testid={`j911-scan-delete-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {activeScan && (
        <Card data-testid="j911-active-scan-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                {polling && <Loader2 className="w-4 h-4 animate-spin" />}
                Scan {activeScan.id?.slice(0, 8)} — {activeScan.status}
              </CardTitle>
              {(activeScan.findings || []).some((f) => !f.ai_verdict && f.admin_decision === 'pending') && (
                <div className="flex items-center gap-3">
                  {analyzingAll && bulkProgress && (
                    <div className="flex items-center gap-2" data-testid="j911-bulk-progress">
                      <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-600 transition-all"
                          style={{ width: `${bulkProgress.total ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{bulkProgress.done} / {bulkProgress.total}</span>
                    </div>
                  )}
                  <Button
                    size="sm"
                    onClick={analyzeAllFindings}
                    disabled={analyzingAll}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="j911-analyze-all-button"
                  >
                    {analyzingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                    {analyzingAll ? 'Analyzing in background…' : `Analyze All with AI (${(activeScan.findings || []).filter((f) => !f.ai_verdict).length})`}
                  </Button>
                </div>
              )}
            </div>
            {activeScan.status === 'failed' && (
              <CardDescription className="text-red-600">{activeScan.error}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {(() => {
              const pendingClean = (activeScan.findings || []).filter((f) => f.admin_decision === 'pending' && f.ai_verdict === 'clean').length;
              const pendingMalicious = (activeScan.findings || []).filter((f) => f.admin_decision === 'pending' && f.ai_verdict === 'malicious').length;
              if (pendingClean === 0 && pendingMalicious === 0) return null;
              return (
                <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="j911-bulk-resolve-panel">
                  <span className="text-xs font-medium text-blue-800 mr-1">Bulk Resolve:</span>
                  {pendingClean > 0 && (
                    <Button
                      size="sm" variant="outline" disabled={bulkResolving}
                      onClick={() => bulkResolve('clean', 'false_positive')}
                      data-testid="j911-bulk-mark-false-positive-button"
                    >
                      {bulkResolving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                      Mark All Clean ({pendingClean}) as False Positive
                    </Button>
                  )}
                  {pendingMalicious > 0 && (
                    <Button
                      size="sm" variant="destructive" disabled={bulkResolving}
                      onClick={() => bulkResolve('malicious', 'confirmed_malicious', 'delete')}
                      data-testid="j911-bulk-mark-deleted-button"
                    >
                      {bulkResolving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Mark All Malicious ({pendingMalicious}) as Deleted
                    </Button>
                  )}
                </div>
              );
            })()}
            {(!activeScan.findings || activeScan.findings.length === 0) && activeScan.status === 'completed' && (
              <p className="text-sm text-green-600 flex items-center gap-2" data-testid="j911-clean-message"><ShieldCheck className="w-4 h-4" /> No malware signatures detected.</p>
            )}
            <div className="space-y-3">
              {(activeScan.findings || []).map((f) => (
                <div key={f.id} className={`p-3 rounded-lg border ${f.admin_decision !== 'pending' ? 'opacity-60' : ''}`} data-testid={`j911-finding-${f.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {f.item_type === 'sql' ? <Database className="w-4 h-4 text-gray-500" /> : <FileWarning className="w-4 h-4 text-gray-500" />}
                        <span className="font-mono text-xs">{f.path}</span>
                        <Badge className={SEVERITY_COLORS[f.severity]}>{f.severity}</Badge>
                      </div>
                      <p className="text-xs text-gray-500">{f.rules_matched.join(', ')}</p>
                      <pre className="text-xs bg-gray-900 text-gray-100 p-2 rounded mt-1 max-w-xl overflow-x-auto">{f.snippet_preview}</pre>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {f.admin_decision === 'pending' && !f.ai_verdict && (
                        <Button size="sm" onClick={() => analyzeFinding(f.id)} disabled={analyzing[f.id] || analyzingAll} data-testid={`j911-analyze-${f.id}`}>
                          {analyzing[f.id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                          Analyze with AI
                        </Button>
                      )}
                      {f.admin_decision !== 'pending' && (
                        <Badge variant="outline" data-testid={`j911-resolution-badge-${f.id}`}>
                          {f.admin_decision === 'false_positive'
                            ? 'False Positive'
                            : { clean: 'Cleaned', delete: 'Deleted', manual: 'Flagged for Manual Review' }[f.resolution_action || 'manual']}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {f.ai_verdict && (
                    <div className="mt-2 p-2 bg-white rounded border">
                      <Badge className={VERDICT_COLORS[f.ai_verdict] || 'bg-gray-400'}>{f.ai_verdict}</Badge>
                      <span className="text-xs text-gray-500 ml-2">{Math.round((f.ai_confidence || 0) * 100)}% confidence</span>
                      <p className="text-sm mt-1">{f.ai_explanation}</p>
                      <p className="text-xs text-gray-600 mt-1"><strong>Recommendation:</strong> {f.ai_recommendation}</p>
                      {f.admin_decision === 'pending' && f.item_type === 'file' && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Button
                            size="sm" variant="destructive"
                            disabled={!f.ai_cleaned_code}
                            title={!f.ai_cleaned_code ? 'AI did not provide a cleaned version for this file' : ''}
                            onClick={() => resolveFinding(f.id, 'confirmed_malicious', 'clean')}
                            data-testid={`j911-clean-file-${f.id}`}
                          ><Wrench className="w-3 h-3 mr-1" />Clean This File</Button>
                          <Button size="sm" variant="destructive" onClick={() => resolveFinding(f.id, 'confirmed_malicious', 'delete')} data-testid={`j911-delete-file-${f.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" />Delete This File
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => resolveFinding(f.id, 'confirmed_malicious', 'manual')} data-testid={`j911-flag-manual-${f.id}`}>
                            Flag for Manual Review
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => resolveFinding(f.id, 'false_positive')} data-testid={`j911-false-positive-${f.id}`}>False Positive</Button>
                        </div>
                      )}
                      {f.admin_decision === 'pending' && f.item_type === 'sql' && (
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="destructive" onClick={() => resolveFinding(f.id, 'confirmed_malicious', 'manual')} data-testid={`j911-confirm-malicious-${f.id}`}>Confirm Malicious</Button>
                          <Button size="sm" variant="outline" onClick={() => resolveFinding(f.id, 'false_positive')} data-testid={`j911-false-positive-${f.id}`}>False Positive</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {Object.keys(components).length > 0 && ['ai_review', 'completed'].includes(activeScan.status) && (
              <div className="mt-5 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="j911-components-panel">
                <p className="text-sm font-semibold text-orange-900 flex items-center gap-2 mb-1">
                  <Boxes className="w-4 h-4" /> Whole-Component Removal Candidates
                </p>
                <p className="text-xs text-orange-700 mb-3">
                  These extensions have {componentThreshold}+ flagged files each — likely fully compromised. Analyze with AI, then explicitly remove the entire
                  component (files + matching database tables) if confirmed. Nothing is ever removed automatically.
                </p>
                <div className="space-y-3">
                  {Object.entries(components).map(([key, c]) => (
                    <div key={key} className="p-3 bg-white rounded border" data-testid={`j911-component-${key}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <span className="font-mono text-sm font-medium">{key}</span>
                          <span className="text-xs text-gray-500 ml-2">{c.file_count} flagged file(s)</span>
                          {c.removed && <Badge className="ml-2 bg-green-600 text-white" data-testid={`j911-component-removed-badge-${key}`}>Removed</Badge>}
                        </div>
                        {!c.removed && !c.analyzed_at && (
                          <Button
                            size="sm" onClick={() => analyzeComponent(key)} disabled={analyzingComponent[key]}
                            data-testid={`j911-component-analyze-button-${key}`}
                          >
                            {analyzingComponent[key] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                            Analyze Component with AI
                          </Button>
                        )}
                      </div>
                      {c.analyzed_at && !c.removed && (
                        <div className="mt-2 p-2 bg-gray-50 rounded border" data-testid={`j911-component-analysis-${key}`}>
                          <Badge className={c.ai_recommend_full_removal ? 'bg-red-600 text-white' : 'bg-gray-400 text-white'}>
                            {c.ai_recommend_full_removal ? 'AI Recommends Full Removal' : 'AI Recommends File-by-File Cleaning'}
                          </Badge>
                          <span className="text-xs text-gray-500 ml-2">{Math.round((c.ai_confidence || 0) * 100)}% confidence</span>
                          <p className="text-sm mt-1">{c.ai_explanation}</p>
                          {c.available_tables && c.available_tables.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-600 mb-1">Database tables to strip from the .sql backup (AI-suggested pre-checked — review before removing):</p>
                              <div className="flex flex-wrap gap-2">
                                {c.available_tables.map((t) => (
                                  <label key={t} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded cursor-pointer" data-testid={`j911-component-table-checkbox-${key}-${t}`}>
                                    <input
                                      type="checkbox"
                                      checked={(selectedTables[key] || []).includes(t)}
                                      onChange={() => toggleComponentTable(key, t)}
                                    />
                                    <code>{t}</code>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          <Button
                            size="sm" variant="destructive" className="mt-3"
                            onClick={() => removeComponent(key)}
                            disabled={removingComponent[key]}
                            data-testid={`j911-component-remove-button-${key}`}
                          >
                            {removingComponent[key] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                            Remove This Entire Component
                          </Button>
                        </div>
                      )}
                      {c.removed && (
                        <p className="text-xs text-green-700 mt-1" data-testid={`j911-component-removed-note-${key}`}>
                          Removed on {c.removed_at ? new Date(c.removed_at).toLocaleString() : ''}
                          {c.tables_stripped && c.tables_stripped.length > 0 && ` — stripped tables: ${c.tables_stripped.join(', ')}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(activeScan.status === 'completed' || activeScan.status === 'finalizing') && (
              <div className="mt-5 p-4 bg-gray-50 border rounded-lg flex items-center justify-between flex-wrap gap-3" data-testid="j911-finalize-panel">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {activeScan.status === 'finalizing' ? 'Applying fixes and building your downloads…' : 'All findings resolved. Ready to apply fixes and generate downloads.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">This applies your Clean/Delete decisions to the extracted files, then builds a report + downloadable zips.</p>
                </div>
                <Button onClick={finalizeScan} disabled={finalizing || activeScan.status === 'finalizing'} className="bg-red-600 hover:bg-red-700" data-testid="j911-finalize-button">
                  {(finalizing || activeScan.status === 'finalizing') ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
                  {(finalizing || activeScan.status === 'finalizing') ? 'Applying fixes…' : 'Finalize & Generate Downloads'}
                </Button>
              </div>
            )}

            {activeScan.status === 'finalized' && (
              <div className="mt-5 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="j911-downloads-panel">
                <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Finalized —
                  {activeScan.actions_applied && ` ${activeScan.actions_applied.cleaned || 0} cleaned, ${activeScan.actions_applied.deleted || 0} deleted, ${activeScan.actions_applied.manual || 0} need manual review.`}
                </p>
                <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1.5" data-testid="j911-retention-notice">
                  <Clock className="w-3.5 h-3.5" />
                  {activeScan.files_purged
                    ? 'The downloadable zip files were auto-deleted 15 days after finalizing (report below remains available).'
                    : `Downloadable zip files are only kept for 15 days from finalizing (until ${activeScan.files_available_until ? new Date(activeScan.files_available_until).toLocaleDateString() : ''}) — the report stays available indefinitely.`}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => downloadArtifact('report')} data-testid="j911-download-report">
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> Download Report
                  </Button>
                  <Button size="sm" variant="outline" disabled={activeScan.files_purged} onClick={() => downloadArtifact('fixed-files')} data-testid="j911-download-fixed-files">
                    <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download Fixed Files Only
                  </Button>
                  <Button size="sm" variant="outline" disabled={activeScan.files_purged} onClick={() => downloadArtifact('full-site')} data-testid="j911-download-full-site">
                    <Package className="w-3.5 h-3.5 mr-1.5" /> Download Full Cleaned Site (.zip)
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DevJoomla911Scanner;
